<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="500px"
  >
    <template v-slot:activator="{ on:dialog }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on:tooltip }">
          <v-btn
            icon
            small
            v-on="{...dialog,...tooltip}"
          >
            <v-icon color="primary">
              mdi-trending-up
            </v-icon>
          </v-btn>
        </template>
        <span>Increment / Decrement</span>
      </v-tooltip>
    </template>
    
    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Increment / Decrement"
      text="New Salary"
      class="mb-0"
    >
      <v-container v-if="dialog">
        <v-row no-gutters>
          <v-col cols="12">
            <h4 class="font-weight-bold">
              Current Basic Salary: RM {{ thousandSeparator(localBasicSalary.amount) }}
            </h4>
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="salaryAmount"
              v-validate="'required|validAmount'"
              v-money="money"
              label="New Salary"
              :error-messages="errors.collect('salaryAmount')"
              data-vv-name="salaryAmount"     
              data-vv-as="salary"
              prefix="RM"
              outlined
            />
          </v-col>

          <v-col cols="12">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
              max-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="startDateDisplay"
                  v-validate="'required'"
                  :error-messages="errors.collect('startDateDisplay')"
                  data-vv-name="startDateDisplay"
                  data-vv-as="effective date"
                  label="Effective Date"
                  outlined
                  class="label-position"
                  readonly
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon>
                      mdi-calendar
                    </v-icon>
                  </template>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="incrementEffectiveDate"
                no-title
                :min="this.$moment(basicSalary.start_date).add(1,'d').format('YYYY-MM-DD')"
                @input="startDateMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col
            cols="12"
            class="pb-0"
          >
            <v-autocomplete
              v-model="salaryType"
              v-validate="'required'"
              :error-messages="errors.collect('salaryType')"
              :items="salaryTypes"
              data-vv-name="salaryType"     
              data-vv-as="salary type"
              item-text="name"
              item-value="value"
              label="Salary Type"
              outlined
            />
          </v-col>

          <v-col cols="12">
            <v-checkbox
              v-model="requestELetter"
            >
              <template v-slot:label>
                <div>
                  e-Letter
                </div>
              </template>
            </v-checkbox>            
          </v-col>          

          <v-col
            v-if="requestELetter"
            cols="12"
            class="pb-0"
          >
            <v-autocomplete
              v-model="letterType"
              v-validate="'required'"
              :error-messages="errors.collect('letterType')"
              :items="letterTypes"
              data-vv-name="letterType"     
              data-vv-as="letter type"
              item-text="name"
              item-value="value"
              label="Letter Type"
              outlined
            />
          </v-col>

          <v-col
            v-if="requestELetter"
            cols="12"
            class="pb-0"
          >
            <v-menu
              v-model="letterDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="letterDateDisplay"
                  v-validate="'required'"
                  outlined
                  readonly
                  label="Letter Date"
                  :error-messages="errors.collect('letterDateDisplay')"
                  data-vv-name="letterDateDisplay"    
                  data-vv-as="letter date"
                  :class="{
                    'required':!letterDateDisplay
                  }"
                  class="label-position" 
                  v-on="on"
                >
                  <template v-slot:prepend-inner>
                    <v-icon>
                      mdi-calendar
                    </v-icon>
                  </template>
                  <v-tooltip 
                    slot="append"
                    top
                  >
                    <template v-slot:activator="{ on }">
                      <v-icon
                        color="warning"
                        v-on="on"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <span>Will based on this date to stamp the <u>latest</u> relevant position in the letter</span>
                  </v-tooltip>
                </v-text-field>
              </template>
              <v-date-picker
                v-model="letterDate"
                no-title
                @input="letterDateMenu = false"
              />
            </v-menu>
          </v-col>
        </v-row>
      </v-container>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          class="px-10"
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          color="hrissecondary"    
          class="white--text px-10" 
          :loading="progressing"
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  import { VMoney } from 'v-money'
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    directives: { money: VMoney },
    props: {
      basicSalary: {
        type: Object,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        localBasicSalary: null,
        startDateMenu: false,
        requestELetter: true,
        incrementEffectiveDate: null,
        letterType: null,
        letterTypes: [
          {
            name: 'Increment Letter',
            value: 'increment',
          },
          {
            name: 'Promotion Letter',
            value: 'promotion',
          },
          {
            name: 'Salary Adjustment Letter',
            value: 'adjustment',
          },
        ],
        salaryType: null,
        salaryTypes: [
          {
            name: 'Monthly Basis',
            value: 'monthly',
          },
          {
            name: 'Daily Basis',
            value: 'daily',
          },
        ],
        money: {
          decimal: '.',
          thousands: ',',
          precision: 2,
          masked: false, /* doesn't work with directive */
        },
        letterDate: null,
        letterDateMenu: false,
        salaryAmount: 0,
      }
    },
    computed: {
      startDateDisplay () {
        return this.incrementEffectiveDate ? this.$moment(this.incrementEffectiveDate, 'YYYY-MM-DD').format('DD MMM YYYY') : ''
      },
      letterDateDisplay () {
        return this.letterDate ? this.$moment(this.letterDate, 'YYYY-MM-DD').format('DD MMM YYYY') : ''
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) return 
        this.localBasicSalary = { ...this.basicSalary }
        if (this.localBasicSalary.amount) { this.localBasicSalary.amount = (Math.round((this.localBasicSalary.amount / 100) * 100) / 100).toFixed(2) }
      
        this.salaryType = this.localBasicSalary.salary_type
        
        this.$validator.extend('validAmount', {
          getMessage (field, args) {
            return 'New salary amount should not be less than 0'     
          },
          validate (value, args) {
            return value.toString().replaceAll(',', '') >= 0
          },
        })
      },
    },
    methods: {
      thousandSeparator (amount) {
        if (amount !== '' || amount !== undefined || amount !== 0 || amount !== '0' || amount !== null) {
          return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        } else {
          return amount
        }
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return
            
            this.setProgressing(true)

            this.$api.companyEmployeeSalaries().increment(this.basicSalary.id, this.prepareFormData())
              .then(res => {
                this.$snack({ message: res.data.status })
                this.$emit('updated')

                this.setProgressing(false)
                this.dialog = false
                this.$resetData()
              })
          })
      },
      setProgressing (status) {
        this.progressing = status
      },
      prepareFormData () {
        return {
          start_date: this.incrementEffectiveDate,
          amount: this.salaryAmount.toString().replaceAll(',', ''),
          is_e_letter_sent: this.requestELetter === true ? 1 : 0,
          letter_date: this.requestELetter === true ? this.letterDate : null,
          letter_type: this.requestELetter === true ? this.letterType : null,
          salary_type: this.salaryType,
        }
      },
    },
  }
</script>

<style scoped>
.label-position >>> .v-label.v-label--active{
  margin-left:-28px;
  background:white;
  padding-right:5px
}
</style>
